/**
 * assetPeripheralService — typed wrapper around the asset-centric
 * peripheral endpoints in fleet-api: /api/assets/:id/peripherals and
 * /api/assets/:id/peripheral-events. The endpoints are brand-agnostic;
 * Jointech, MaxVision and JIMI balises all flow through here, the
 * capability is inferred from device_type.
 *
 * These endpoints answer two questions the asset-centric workspace needs
 * but the existing /api/jimi/* device-centric surface cannot:
 *
 *   1) "When the user clicks asset X, which peripherals are currently
 *      attached, and what type are they?" — drives the picker modal and
 *      the 0/1/N auto-open rule in CompleteFleetDashboard.
 *   2) "What does the asset's lifetime peripheral event timeline look
 *      like?" — joins events with balise_assignment_history so events
 *      from detached peripherals stay attributed to the right asset
 *      slice.
 *
 * The asset id can be a bare numeric/UUID, an ASSET-prefixed id or a
 * CONTAINER-prefixed id; the backend handles the prefixing convention.
 */
import { apiClient } from '../config/api'

export type PeripheralCapability =
  | 'SMART_LOCK'
  | 'GPS_TRACKER'
  | 'AI_DASHCAM'
  | 'OBD'
  | 'TAG'

export interface AssetPeripheral {
  id: number
  imei: string | null
  lockCode: string
  universalId: string
  /** Brand of the underlying balise (JIMI / Jointech / MaxVision / …). */
  brand?: string | null
  deviceName?: string | null
  /** Raw orchestration_balises.device_type value (may be null). */
  deviceType?: string | null
  /** Inferred capability used to pick the right asset-scoped window. */
  capability: PeripheralCapability
  assignmentStatus?: string | null
  batteryLevel?: number | null
  signalStrength?: number | null
  lat?: number | null
  lng?: number | null
  lastSeen?: string | null
  assetId?: number | string | null
  containerId?: number | string | null
}

export interface AssetPeripheralsResponse {
  assetId: string
  assetType: 'asset' | 'container' | null
  count: number
  peripherals: AssetPeripheral[]
}

export interface AssetPeripheralEventRow {
  id?: number
  timestamp?: string
  event_type?: string
  severity?: string | null
  lock_code?: string
  latitude?: string | number | null
  longitude?: string | number | null
  speed?: string | number | null
  heading?: string | number | null
  source_brand?: string | null
  source_system?: string | null
  imei?: string | null
  device_type?: string | null
  device_name?: string | null
  [k: string]: any
}

export interface AssetPeripheralEventsResponse {
  assetId: string
  assetType: 'asset' | 'container' | null
  count: number
  events: AssetPeripheralEventRow[]
}

const enc = encodeURIComponent

export const assetPeripheralService = {
  /**
   * Returns every balise currently attached to the given asset, regardless
   * of brand. The picker uses this to render one card per attached
   * peripheral.
   */
  async getAssetPeripherals(assetId: string | number): Promise<AssetPeripheralsResponse> {
    const { data } = await apiClient.get(`/assets/${enc(String(assetId))}/peripherals`)
    return data as AssetPeripheralsResponse
  },

  /**
   * Returns the asset's peripheral event timeline, joined with
   * balise_assignment_history so detached peripherals keep their past
   * events on the asset for the period they were attached. Brand-blind:
   * pass `brand` if you want to scope the result to a single vendor.
   */
  async getAssetPeripheralEvents(assetId: string | number, opts: {
    from?: string
    to?: string
    hours?: number
    peripheralType?: PeripheralCapability | string
    brand?: string
    limit?: number
  } = {}): Promise<AssetPeripheralEventsResponse> {
    const params: Record<string, string | number> = {}
    if (opts.from)           params.from = opts.from
    if (opts.to)             params.to   = opts.to
    if (opts.hours)          params.hours = opts.hours
    if (opts.peripheralType) params.peripheralType = String(opts.peripheralType)
    if (opts.brand)          params.brand = String(opts.brand)
    if (opts.limit)          params.limit = opts.limit
    const { data } = await apiClient.get(`/assets/${enc(String(assetId))}/peripheral-events`, params)
    return data as AssetPeripheralEventsResponse
  },
}

// ---------------------------------------------------------------------------
// Helpers shared by the picker modal + the asset-scoped windows
// ---------------------------------------------------------------------------

export function peripheralCapabilityLabel(c: PeripheralCapability | string | null | undefined): string {
  switch (c) {
    case 'SMART_LOCK':   return 'Smart Lock'
    case 'GPS_TRACKER':  return 'GPS Tracker'
    case 'AI_DASHCAM':   return 'AI Dashcam'
    case 'OBD':          return 'OBD'
    case 'TAG':          return 'BLE Tag'
    default:             return 'Unclassified'
  }
}

/**
 * Maps a peripheral capability to the dock window id the dashboard should
 * open when the user picks the corresponding card. GPS_TRACKER reuses the
 * existing fleetMap window; everything else maps to a dedicated
 * asset-scoped window registered in CompleteFleetDashboard.
 */
export function peripheralCapabilityToWindowId(c: PeripheralCapability | string): string {
  switch (c) {
    case 'SMART_LOCK':  return 'assetSmartLock'
    case 'AI_DASHCAM':  return 'assetDashcam'
    case 'OBD':         return 'assetObd'
    case 'TAG':         return 'assetTags'
    case 'GPS_TRACKER': return 'fleetMap'
    default:            return 'fleetMap'
  }
}
